/**
 * Switch — Base UI Switch primitive (toggle).
 *
 * Design system mapping:
 *   • Track: h-6 w-11 rounded-full border-2 border-transparent
 *     - off: bg-gray-300 dark:bg-gray-600
 *     - on:  bg-blue-600
 *   • Thumb: h-5 w-5 rounded-full bg-white shadow, translate-x-5 when on
 *     - off: cancel-bold icon, text-gray-500
 *     - on:  check-bold icon, text-blue-600
 *   • Focus: ring-2 blue-500 ring-offset-2
 *   • Disabled: opacity-50
 */
import * as React from "react"
import { Switch as BaseSwitch } from "@base-ui-components/react/switch"
import CheckIcon from "../../icons/cisco-momentum/core/check-bold.svg?react"
import CancelIcon from "../../icons/cisco-momentum/core/cancel-bold.svg?react"
import { cn } from "@lib/cn"

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof BaseSwitch.Root> {
  /** Optional label rendered to the right of the track. */
  label?: React.ReactNode
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, label, ...props }, ref) => {
    const labelId = React.useId()
    const track = (
      <BaseSwitch.Root
        ref={ref as React.Ref<HTMLElement>}
        // The root is a span with role="switch", which a wrapping <label> cannot name.
        aria-labelledby={label ? labelId : undefined}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
          "bg-gray-300 dark:bg-gray-600",
          "data-[checked]:bg-blue-600",
          "transition-colors duration-200 ease-in-out motion-reduce:transition-none",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
          "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
          className,
        )}
        {...props}
      >
        <BaseSwitch.Thumb
          className={cn(
            "pointer-events-none inline-flex items-center justify-center h-5 w-5",
            "transform rounded-full bg-white shadow ring-0",
            "transition duration-200 ease-in-out motion-reduce:transition-none",
            "data-[checked]:translate-x-5",
            "[&_.check]:hidden [&_.cancel]:block",
            "data-[checked]:[&_.check]:block data-[checked]:[&_.cancel]:hidden",
          )}
        >
          <CheckIcon className="check w-2.5 h-2.5 text-blue-600" aria-hidden="true" />
          <CancelIcon className="cancel w-2.5 h-2.5 text-gray-500" aria-hidden="true" />
        </BaseSwitch.Thumb>
      </BaseSwitch.Root>
    )
    if (label) {
      return (
        <label className="inline-flex items-center gap-2.5 text-sm text-webex-gray-1000 dark:text-white cursor-pointer select-none">
          {track}
          <span id={labelId}>{label}</span>
        </label>
      )
    }
    return track
  },
)
Switch.displayName = "Switch"
